import type { PaginatedExtendedGroupByQuery } from '@/api/generated/model';
import { MULTIPLE_COLORS } from '@/constants/ui.constants';

export type AiSpendOrgSnapshot = {
  totalCost: number | null;
  totalCostPrev: number | null;
  tasks: number;
  spendPerTask: number | null;
};

export type TeamSpendSnapshot = {
  key: string;
  name: string;
  cost: number;
  tasks: number;
  spendPerTask: number | null;
  shareOfTotal: number;
  color: string;
};

export type CompositionSegment = {
  key: string;
  label: string;
  value: number;
  share: number;
  color: string;
  isOther?: boolean;
  otherCount?: number;
  isUnclassified?: boolean;
};

export function computeSpendPerTask(cost: number | null | undefined, tasks: number): number | null {
  if (cost == null || tasks <= 0) return null;
  return cost / tasks;
}

export function mapOrgSpendSnapshot(data: PaginatedExtendedGroupByQuery): AiSpendOrgSnapshot {
  const row = data.data[0];
  const totalCost = row?.cost ?? null;
  const totalCostPrev = row?.cost_prev ?? null;
  const tasks = row?.n_segments ?? 0;

  return {
    totalCost,
    totalCostPrev,
    tasks,
    spendPerTask: computeSpendPerTask(totalCost, tasks),
  };
}

export function mapTeamSpendSnapshots(data: PaginatedExtendedGroupByQuery, totalCost: number): TeamSpendSnapshot[] {
  const denominator = totalCost > 0 ? totalCost : 0;

  return data.data
    .map((row, index) => {
      const name = row.group_name?.trim() || 'Unknown';
      const cost = row.cost ?? 0;
      const tasks = row.n_segments ?? 0;

      return {
        key: name,
        name,
        cost,
        tasks,
        spendPerTask: computeSpendPerTask(cost, tasks),
        shareOfTotal: denominator > 0 ? cost / denominator : 0,
        color: MULTIPLE_COLORS[index % MULTIPLE_COLORS.length]!,
      };
    })
    .sort((left, right) => right.cost - left.cost);
}

export function filterTeamsWithTasks(teams: TeamSpendSnapshot[]): TeamSpendSnapshot[] {
  return teams.filter((team) => team.tasks > 0);
}

export function sortTeamsBySpendPerTaskDesc(teams: TeamSpendSnapshot[]): TeamSpendSnapshot[] {
  return [...teams].sort((left, right) => (right.spendPerTask ?? 0) - (left.spendPerTask ?? 0));
}
